import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, Users, TrendingUp, Calendar, FileText, 
  Building2, Landmark, FileCheck, AlertCircle, CheckCircle,
  Clock, ArrowRight, BarChart3, Play, Settings, CreditCard,
  Banknote, Receipt, Gift, PiggyBank, Calculator, ChevronRight,
  Flag, Globe, Wallet, AlertTriangle, ArrowUpRight, ArrowDownRight,
  Sparkles
} from 'lucide-react';
import PayrollAIInsights from './PayrollAIInsights';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { db } from '../../../lib/db';
import { formatUSDCurrency } from '../../../lib/usPayrollCalculations';
import { toast } from 'sonner';

export default function UnifiedPayrollDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalEmployees: 0,
    configuredEmployees: 0,
    activePayGroups: 0,
    pendingPayrolls: 0,
    pendingLiabilities: 0,
    pendingReimbursements: 0,
    pendingBonuses: 0,
    lastPayrollDate: null,
    lastPayrollTotal: 0,
    nextPayDate: null,
    ytdGrossPay: 0,
    ytdTaxes: 0,
    ytdNetPay: 0,
    monthlyPayroll: 0,
    monthlyTaxes: 0
  });
  const [recentPayrolls, setRecentPayrolls] = useState([]);
  const [setupProgress, setSetupProgress] = useState(0);
  const [setupItems, setSetupItems] = useState([]);
  const [payrollMode, setPayrollMode] = useState('us'); // 'us' or 'international'
  const [showAIInsights, setShowAIInsights] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [
        users,
        companySettings,
        payGroups,
        payCodes,
        compensation,
        w4Data,
        payrollRuns,
        payrollItems,
        taxLiabilities,
        reimbursements,
        bonuses,
        salaryStructures
      ] = await Promise.all([
        db.users.toArray(),
        db.companySettings.toArray(),
        db.payGroups.toArray(),
        db.payCodes.toArray(),
        db.employeeCompensation.toArray(),
        db.employeeW4.toArray(),
        db.payrollRuns.toArray(),
        db.payrollRunItems.toArray(),
        db.taxLiabilities.toArray(),
        db.reimbursements.toArray(),
        db.bonuses.toArray(),
        db.salaryStructures.toArray()
      ]);

      const employees = users.filter(u => u.role !== 'admin');
      
      // Check US configuration
      const configuredEmps = employees.filter(e => 
        compensation.some(c => c.userId === e.id) &&
        w4Data.some(w => w.userId === e.id)
      );

      // Calculate YTD totals
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1).getTime();
      const monthStart = new Date(currentYear, new Date().getMonth(), 1).getTime();
      
      const ytdItems = payrollItems.filter(item => item.createdAt >= yearStart);
      const monthItems = payrollItems.filter(item => item.createdAt >= monthStart);
      
      const ytdGrossPay = ytdItems.reduce((sum, item) => sum + (item.grossPay || 0), 0);
      const ytdTaxes = ytdItems.reduce((sum, item) => 
        sum + (item.federalTax || 0) + (item.stateTax || 0) + 
        (item.socialSecurity || 0) + (item.medicare || 0), 0);
      const ytdNetPay = ytdItems.reduce((sum, item) => sum + (item.netPay || 0), 0);
      
      const monthlyPayroll = monthItems.reduce((sum, item) => sum + (item.grossPay || 0), 0);
      const monthlyTaxes = monthItems.reduce((sum, item) => 
        sum + (item.federalTax || 0) + (item.stateTax || 0) + 
        (item.socialSecurity || 0) + (item.medicare || 0), 0);

      // Get recent payrolls
      const completedRuns = payrollRuns
        .filter(r => r.status === 'completed' || r.status === 'submitted')
        .sort((a, b) => (b.processedAt || b.submittedAt) - (a.processedAt || a.submittedAt))
        .slice(0, 5);

      const recentPayrollData = completedRuns.map(run => {
        const items = payrollItems.filter(i => i.payrollRunId === run.id);
        // Use stored totals from run if no items found, or calculate from items
        const hasItems = items.length > 0;
        return {
          ...run,
          employeeCount: hasItems ? items.length : (run.employeeCount || 0),
          totalGross: hasItems ? items.reduce((sum, i) => sum + (i.grossPay || 0), 0) : (run.totalGross || 0),
          totalNet: hasItems ? items.reduce((sum, i) => sum + (i.netPay || 0), 0) : (run.totalNet || run.summary?.totalNet || 0)
        };
      });

      // Pending items
      const pendingReimb = reimbursements.filter(r => r.status === 'pending');
      const pendingBonus = bonuses.filter(b => b.status === 'pending');
      const pendingLiab = taxLiabilities.filter(l => l.status === 'pending' || l.status === 'due');

      // Setup progress calculation
      const setupChecks = [
        { label: 'Company Tax Settings', complete: companySettings.length > 0, href: '/admin/payroll/company-tax' },
        { label: 'Pay Groups', complete: payGroups.length > 0, href: '/admin/payroll/pay-groups' },
        { label: 'Pay Codes', complete: payCodes.length > 0, href: '/admin/payroll/pay-codes' },
        { label: 'Employee Compensation', complete: configuredEmps.length > 0, href: '/admin/payroll/employee-setup' },
        { label: 'Employee W-4 Setup', complete: w4Data.length > 0, href: '/admin/payroll/employee-setup' }
      ];
      
      const completedSetup = setupChecks.filter(s => s.complete).length;
      const progress = Math.round((completedSetup / setupChecks.length) * 100);

      setStats({
        totalEmployees: employees.length,
        configuredEmployees: configuredEmps.length,
        activePayGroups: payGroups.filter(g => g.status === 'active').length,
        pendingPayrolls: payrollRuns.filter(r => r.status === 'draft' || r.status === 'processing').length,
        pendingLiabilities: pendingLiab.length,
        pendingReimbursements: pendingReimb.length,
        pendingBonuses: pendingBonus.length,
        lastPayrollDate: completedRuns[0]?.processedAt,
        lastPayrollTotal: recentPayrollData[0]?.totalNet || 0,
        nextPayDate: getNextPayDate(payGroups),
        ytdGrossPay,
        ytdTaxes,
        ytdNetPay,
        monthlyPayroll,
        monthlyTaxes
      });

      setRecentPayrolls(recentPayrollData);
      setSetupProgress(progress);
      setSetupItems(setupChecks);

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getNextPayDate = (payGroups) => {
    if (!payGroups || payGroups.length === 0) return null;
    const today = new Date();
    const activeGroups = payGroups.filter(g => g.status === 'active');
    if (activeGroups.length === 0) return null;
    
    // Simple calculation - find next Friday
    const dayOfWeek = 5; // Friday
    const daysUntil = (dayOfWeek - today.getDay() + 7) % 7 || 7;
    return new Date(today.getTime() + daysUntil * 86400000);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-teal-600" />
            Payroll Management
          </h1>
          <p className="text-gray-500 mt-1">Comprehensive payroll processing with US tax compliance</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={showAIInsights ? "default" : "outline"}
            onClick={() => setShowAIInsights(!showAIInsights)}
            className={showAIInsights ? "bg-gradient-to-r from-violet-600 to-purple-600" : ""}
          >
            <Sparkles className="w-4 h-4 mr-2" /> AI Insights
          </Button>
          <Button variant="outline" onClick={() => navigate('/admin/payroll/reports')}>
            <BarChart3 className="w-4 h-4 mr-2" /> Reports
          </Button>
          <Button onClick={() => navigate('/admin/payroll/run')}>
            <Play className="w-4 h-4 mr-2" /> Run Payroll
          </Button>
        </div>
      </div>

      {/* Setup Progress (if not complete) */}
      {setupProgress < 100 && (
        <Card className="border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-amber-800">Complete Your Payroll Setup</h3>
                  <Badge variant="outline" className="text-amber-700 border-amber-300">
                    {setupProgress}% Complete
                  </Badge>
                </div>
                <Progress value={setupProgress} className="h-2 mb-4" />
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {setupItems.map((item, i) => (
                    <button
                      key={i}
                      onClick={() => !item.complete && navigate(item.href)}
                      className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium transition-all ${
                        item.complete 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-white text-amber-700 hover:bg-amber-100 cursor-pointer shadow-sm'
                      }`}
                    >
                      {item.complete ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Clock className="w-4 h-4" />
                      )}
                      <span className="truncate">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Insights Panel */}
      {showAIInsights && (
        <PayrollAIInsights />
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-teal-50 to-emerald-50 border-teal-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-teal-600" />
              <Badge variant="outline" className="text-teal-700 border-teal-200">YTD</Badge>
            </div>
            <p className="text-2xl font-bold text-teal-900">{formatUSDCurrency(stats.ytdGrossPay)}</p>
            <p className="text-sm text-teal-600">Total Gross Payroll</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Landmark className="w-8 h-8 text-blue-600" />
              <Badge variant="outline" className="text-blue-700 border-blue-200">YTD</Badge>
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatUSDCurrency(stats.ytdTaxes)}</p>
            <p className="text-sm text-blue-600">Taxes Withheld</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Banknote className="w-8 h-8 text-emerald-600" />
              <Badge variant="outline" className="text-emerald-700 border-emerald-200">YTD</Badge>
            </div>
            <p className="text-2xl font-bold text-emerald-900">{formatUSDCurrency(stats.ytdNetPay)}</p>
            <p className="text-sm text-emerald-600">Net Pay Disbursed</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <Users className="w-8 h-8 text-purple-600" />
              <Badge variant="outline" className="text-purple-700 border-purple-200">
                {stats.configuredEmployees}/{stats.totalEmployees}
              </Badge>
            </div>
            <p className="text-2xl font-bold text-purple-900">{stats.totalEmployees}</p>
            <p className="text-sm text-purple-600">Total Employees</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <QuickActionButton
                icon={Play}
                label="Run Payroll"
                description="Process payroll"
                onClick={() => navigate('/admin/payroll/run')}
                variant="primary"
              />
              <QuickActionButton
                icon={Users}
                label="Employee Setup"
                description="W-4 & compensation"
                onClick={() => navigate('/admin/payroll/employee-setup')}
                badge={stats.configuredEmployees < stats.totalEmployees ? 
                  `${stats.totalEmployees - stats.configuredEmployees} pending` : null}
              />
              <QuickActionButton
                icon={Landmark}
                label="Tax Liabilities"
                description="View & pay taxes"
                onClick={() => navigate('/admin/payroll/tax-liabilities')}
                badge={stats.pendingLiabilities > 0 ? `${stats.pendingLiabilities} due` : null}
                badgeVariant="destructive"
              />
              <QuickActionButton
                icon={FileCheck}
                label="Year-End Forms"
                description="W-2 & W-3"
                onClick={() => navigate('/admin/payroll/year-end')}
              />
              <QuickActionButton
                icon={Receipt}
                label="Reimbursements"
                description="Manage requests"
                onClick={() => navigate('/admin/payroll/reimbursements')}
                badge={stats.pendingReimbursements > 0 ? `${stats.pendingReimbursements} pending` : null}
              />
              <QuickActionButton
                icon={Gift}
                label="Bonuses"
                description="Bonus management"
                onClick={() => navigate('/admin/payroll/bonuses')}
                badge={stats.pendingBonuses > 0 ? `${stats.pendingBonuses} pending` : null}
              />
              <QuickActionButton
                icon={CreditCard}
                label="Payslips"
                description="View & distribute"
                onClick={() => navigate('/admin/payroll/payslips')}
              />
              <QuickActionButton
                icon={BarChart3}
                label="Reports"
                description="Payroll reports"
                onClick={() => navigate('/admin/payroll/reports')}
              />
            </div>
          </CardContent>
        </Card>

        {/* Alerts & Notifications */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Alerts
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {stats.pendingLiabilities > 0 && (
              <AlertItem
                icon={Landmark}
                title="Tax Liabilities Due"
                description={`${stats.pendingLiabilities} tax payment(s) pending`}
                variant="danger"
                onClick={() => navigate('/admin/payroll/tax-liabilities')}
              />
            )}
            {stats.configuredEmployees < stats.totalEmployees && (
              <AlertItem
                icon={Users}
                title="Incomplete Setup"
                description={`${stats.totalEmployees - stats.configuredEmployees} employee(s) need setup`}
                variant="warning"
                onClick={() => navigate('/admin/payroll/employee-setup')}
              />
            )}
            {stats.pendingReimbursements > 0 && (
              <AlertItem
                icon={Receipt}
                title="Pending Reimbursements"
                description={`${stats.pendingReimbursements} request(s) awaiting approval`}
                variant="info"
                onClick={() => navigate('/admin/payroll/reimbursements')}
              />
            )}
            {stats.nextPayDate && (
              <AlertItem
                icon={Calendar}
                title="Next Pay Date"
                description={formatDate(stats.nextPayDate)}
                variant="default"
              />
            )}
            {stats.pendingLiabilities === 0 && stats.configuredEmployees === stats.totalEmployees && 
             stats.pendingReimbursements === 0 && (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <span className="text-sm text-emerald-700">All caught up!</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Configuration & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-400" />
              Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <ConfigLink
              icon={Building2}
              label="Company Tax Settings"
              description="EIN, State IDs, Local taxes"
              href="/admin/payroll/company-tax"
            />
            <ConfigLink
              icon={Calendar}
              label="Pay Groups"
              description={`${stats.activePayGroups} active schedule(s)`}
              href="/admin/payroll/pay-groups"
            />
            <ConfigLink
              icon={FileText}
              label="Pay Codes"
              description="Earnings & deductions"
              href="/admin/payroll/pay-codes"
            />
            <ConfigLink
              icon={Calculator}
              label="Salary Structures"
              description="Base pay configuration"
              href="/admin/payroll/salaries"
            />
            <ConfigLink
              icon={Settings}
              label="Payroll Settings"
              description="General settings"
              href="/admin/payroll/settings"
            />
          </CardContent>
        </Card>

        {/* Recent Payrolls */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Payroll Runs</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/payroll/reports')}>
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentPayrolls.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Wallet className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No payroll runs yet</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => navigate('/admin/payroll/run')}
                >
                  Run Your First Payroll
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {recentPayrolls.map((payroll, i) => (
                  <div 
                    key={payroll.id || i}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-teal-100 rounded-lg">
                        <DollarSign className="w-5 h-5 text-teal-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {formatDate(payroll.periodStart)} - {formatDate(payroll.periodEnd)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {payroll.employeeCount} employees • Check date: {formatDate(payroll.checkDate)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-emerald-600">{formatUSDCurrency(payroll.totalNet)}</p>
                      <p className="text-xs text-gray-500">Net Pay</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tax Calendar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            Upcoming Tax Deadlines
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <TaxDeadline 
              date="January 31" 
              title="W-2 Forms Due"
              description="Distribute to employees"
              daysUntil={getDaysUntil(1, 31)}
            />
            <TaxDeadline 
              date="January 31" 
              title="Form 941 Q4"
              description="Quarterly federal return"
              daysUntil={getDaysUntil(1, 31)}
            />
            <TaxDeadline 
              date="February 28" 
              title="W-2/W-3 Filing"
              description="Paper filing to SSA"
              daysUntil={getDaysUntil(2, 28)}
            />
            <TaxDeadline 
              date="April 30" 
              title="Form 941 Q1"
              description="Quarterly federal return"
              daysUntil={getDaysUntil(4, 30)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function QuickActionButton({ icon: Icon, label, description, onClick, variant = 'default', badge, badgeVariant = 'secondary' }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-start p-4 rounded-xl border transition-all hover:shadow-md text-left ${
        variant === 'primary' 
          ? 'bg-gradient-to-br from-teal-500 to-emerald-600 text-white border-transparent hover:from-teal-600 hover:to-emerald-700' 
          : 'bg-white hover:border-teal-200 hover:bg-teal-50'
      }`}
    >
      <div className="flex items-center justify-between w-full mb-2">
        <Icon className={`w-5 h-5 ${variant === 'primary' ? 'text-white' : 'text-teal-600'}`} />
        {badge && (
          <Badge variant={badgeVariant} className="text-xs">
            {badge}
          </Badge>
        )}
      </div>
      <span className={`font-medium text-sm ${variant === 'primary' ? 'text-white' : 'text-gray-900'}`}>
        {label}
      </span>
      <span className={`text-xs ${variant === 'primary' ? 'text-teal-100' : 'text-gray-500'}`}>
        {description}
      </span>
    </button>
  );
}

function AlertItem({ icon: Icon, title, description, variant = 'default', onClick }) {
  const variants = {
    danger: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
    default: 'bg-gray-50 border-gray-200 text-gray-700'
  };
  
  const iconVariants = {
    danger: 'text-red-500',
    warning: 'text-amber-500',
    info: 'text-blue-500',
    default: 'text-gray-500'
  };

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-all hover:shadow-sm ${variants[variant]}`}
    >
      <Icon className={`w-5 h-5 ${iconVariants[variant]}`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm">{title}</p>
        <p className="text-xs opacity-80 truncate">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 opacity-50" />
    </button>
  );
}

function ConfigLink({ icon: Icon, label, description, href }) {
  const navigate = useNavigate();
  
  return (
    <button
      onClick={() => navigate(href)}
      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all text-left group"
    >
      <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-teal-100 transition-colors">
        <Icon className="w-4 h-4 text-gray-600 group-hover:text-teal-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm text-gray-900">{label}</p>
        <p className="text-xs text-gray-500 truncate">{description}</p>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-teal-500" />
    </button>
  );
}

function TaxDeadline({ date, title, description, daysUntil }) {
  const isUrgent = daysUntil <= 14 && daysUntil >= 0;
  const isPast = daysUntil < 0;
  
  return (
    <div className={`p-4 rounded-lg border ${
      isPast ? 'bg-gray-50 border-gray-200' : 
      isUrgent ? 'bg-red-50 border-red-200' : 
      'bg-white border-gray-200'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${
          isPast ? 'text-gray-400' : isUrgent ? 'text-red-600' : 'text-teal-600'
        }`}>
          {date}
        </span>
        {!isPast && (
          <Badge variant={isUrgent ? 'destructive' : 'outline'} className="text-xs">
            {daysUntil === 0 ? 'Today' : `${daysUntil}d`}
          </Badge>
        )}
      </div>
      <p className={`font-medium text-sm ${isPast ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
        {title}
      </p>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  );
}

function getDaysUntil(month, day) {
  const today = new Date();
  const targetYear = today.getFullYear();
  let target = new Date(targetYear, month - 1, day);
  
  if (target < today) {
    target = new Date(targetYear + 1, month - 1, day);
  }
  
  const diff = target.getTime() - today.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
